import EntryConst from '../position-modules/entries/EntryConst';
import { EntryContainer } from '../position-modules/EntryContainer';
import EntryEditable from '../position-modules/entries/EntryEditable';
import { stringToNumber } from '../../special/numberConverter';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSortable } from '@dnd-kit/sortable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';
import SpeedDialMenu from '../../menu/SpeedDialMenu';
import { useTranslation } from 'react-i18next';
import MoneyLabel from '../../label/MoneyLabel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

export const PositionsGroup = ({
  props,
  items,
  item,
  index,
  columns,
  groupKey,
  onChange,
  onFocus,
  onEdit,
  onDelete,
  className,
  isDragging,
  //toggleGroupExpand,
}) => {
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    if (isDragging && isHovering) setIsHovering(false);
  }, [isDragging]);

  // change
  function handleChange(e, col) {
    var newItems = items;
    if (col.number) {
      newItems[index][col.key] = stringToNumber(
        e.target.value,
        col.decimalPlaces ? col.decimalPlaces : 2
      );
    } else {
      newItems[index][col.key] = e.target.value;
    }
    onChange([...newItems]);
  }

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.sortId });

  function checkCol(col) {
    return col.key.includes('position') &&
      col.key != 'positionableId' &&
      col.key != 'positionableType'
      ? col.key.match(/(\d+)$/)[0]
      : null;
  }

  return (
    <>
      <tr
        {...props}
        className={className + ' position-group'}
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        style={{
          transition,
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {
          //item.expandStatus:  0 - Cannot expand/has no children, 1 - is not expanded, 2 - is expanded
          <td>
            {item.expandStatus > 0 && (
              <IconButton
                onClick={
                  () =>
                    handleChange(
                      { target: { value: item.expandStatus == 1 ? 2 : 1 } },
                      { key: 'expandStatus' }
                    )
                  /*toggleGroupExpand({
                    ...item,
                    expandStatus: item.expandStatus == 1 ? 2 : 1,
                  })*/
                }
              >
                <ExpandMoreIcon
                  sx={{
                    transform: `rotate(${
                      item.expandStatus == 2 ? '-180deg' : '0deg'
                    })`,
                    transition: '0.2s',
                  }}
                />
              </IconButton>
            )}
          </td>
        }
        {columns.map((col, i) => (
          <GroupColumn
            key={i}
            col={col}
            groupKey={groupKey}
            item={item}
            isLabel={groupKey === col.key}
            isPosition={checkCol(col) == item.depth}
            onChange={(e) => handleChange(e, col)}
            onShowSubtotal={() =>
              handleChange(
                { target: { value: !item.showSubtotal } },
                { key: 'showSubtotal' }
              )
            }
            onFocus={onFocus}
            onEdit={onEdit}
            onDelete={isHovering ? onDelete : null}
            posCol={checkCol(col)}
            isDragging={isDragging}
          />
        ))}
      </tr>
      <tr>
        <td colSpan="999"></td>
      </tr>
    </>
  );
};

const GroupColumn = ({
  groupKey,
  item,
  isLabel,
  isPosition,
  col,
  onChange,
  onShowSubtotal,
  onFocus,
  onEdit,
  onDelete,
  posCol,
  isDragging,
  isLastCol,
}) => {
  const { t } = useTranslation();

  const actions = [
    {
      icon: <EditIcon />,
      name: t('Edit'),
      onClick: () => onEdit(item),
    },
    {
      icon: <DeleteIcon sx={{ color: 'red' }} />,
      name: t('Delete'),
      onClick: () => onDelete(item),
    },
  ];

  if (col.key == 'delete') {
    return (onDelete && !isDragging) || onEdit ? (
      onEdit ? (
        <td
          className="relative"
          style={{
            width: col.width ? col.width : 'max-content',
            maxWidth: col.width ? col.width : 'unset',
          }}
        >
          <SpeedDialMenu className="absolute top-0 right-0" actions={actions} />
        </td>
      ) : (
        <td
          style={{
            width: col.width ? col.width : 'max-content',
            maxWidth: col.width ? col.width : 'unset',
          }}
        >
          <Box className="w-full flex justify-end">
            <IconButton onClick={() => onDelete(item)}>
              <DeleteIcon sx={{ color: 'red' }} />
            </IconButton>
          </Box>
        </td>
      )
    ) : (
      <td
        style={{
          width: col.width ? col.width : 'max-content',
          maxWidth: col.width ? col.width : 'unset',
        }}
      ></td>
    );
  }
  if (col.hidden) return;

  if (col.key == 'price' || col.key == 'amount') return <td></td>;
  if (col.key == 'custom' && String(item.sumNetto)) {
    return (
      <>
        <td colSpan={1}>
          <Box className="flex justify-end">
            <Tooltip
              title={
                item.showSubtotal ? t('hide subtotal') : t('show subtotal')
              }
            >
              <Button
                size="small"
                onClick={() => onShowSubtotal(!item.showSubtotal)}
              >
                {!item.showSubtotal ? (
                  <VisibilityIcon />
                ) : (
                  <VisibilityOffIcon className=" opacity-30" />
                )}
                <Typography
                  className="monospace pr-4 pl-6"
                  sx={{
                    textDecoration: item.showSubtotal ? 'none' : 'line-through',
                    opacity: item.showSubtotal ? 1 : 0.3,
                  }}
                >
                  <MoneyLabel money={item.sumNetto} />
                </Typography>
              </Button>
            </Tooltip>
          </Box>
        </td>
      </>
    );
  }

  if (isPosition)
    return (
      <EntryContainer
        col={col}
        showBorder={(posCol && posCol <= item.depth) ?? false}
      >
        <EntryConst
          value={item.position ?? null}
          unit=""
          className={col.className}
        />
      </EntryContainer>
    );
  if (!isLabel)
    return (
      <EntryContainer
        col={col}
        showBorder={(posCol && posCol <= item.depth) ?? false}
      />
    );

  return (
    <EntryContainer
      col={col}
      showBorder={(posCol && posCol <= item.depth) ?? false}
    >
      <EntryEditable
        col={col}
        item={item}
        onChange={onChange}
        onFocus={onFocus}
      />
    </EntryContainer>
  );
};
